import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CartItemState } from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { HorizontalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { cart } from '@sorare/core/src/lib/glossary';

import { useCart } from 'hooks/useCart';

import { CartItem } from '../CartItem';
import { ClearButton } from '../ClearButton';
import { EmptyCart } from '../EmptyCart';
import { MainCartItems_cartItem } from './__generated__/index.graphql';

const Header = styled(Horizontal).attrs({ gap: 1 })`
  align-items: baseline;
`;

const Section = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
`;
const Wrapper = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
`;
const CartItemWrapper = styled(Vertical)`
  width: 100%;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
`;

const CartItemSkeleton = styled(Skeleton)`
  width: 100%;
  height: 160px;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
`;

type CartItemsProps = {
  cartItems: MainCartItems_cartItem[];
  count: number;
};
const CartItems = ({ cartItems, count }: CartItemsProps) => {
  return (
    <Wrapper>
      {cartItems.map(cartItem => (
        <CartItemWrapper key={cartItem.id}>
          <CartItem cartItem={cartItem} card={cartItem.card} />
        </CartItemWrapper>
      ))}
      {count - cartItems.length > 0 &&
        Array.from({ length: count - cartItems.length }, (v, i) => i).map(i => (
          <CartItemSkeleton key={i} />
        ))}
    </Wrapper>
  );
};

type Props = {
  cartItems: MainCartItems_cartItem[];
};

export const MainCartItems = ({ cartItems }: Props) => {
  const { formatMessage } = useIntlContext();
  const { cartCount, removedCartCount, removeFromCart } = useCart();

  const availableCartItems = cartItems.filter(
    cartItem => cartItem.state !== CartItemState.REMOVED
  );

  const removedCartItems = cartItems.filter(
    cartItem => cartItem.state === CartItemState.REMOVED
  );

  if (!cartCount && !removedCartCount) return <EmptyCart />;
  return (
    <Vertical>
      <Header>
        <HeadlineS as="h2">
          <FormattedMessage
            id="cartItems.title"
            defaultMessage="{total} {total, plural, one {card} other {Cards}}"
            values={{ total: cartItems.length || cartCount }}
          />
        </HeadlineS>
        <ClearButton
          onClear={async () => {
            await removeFromCart.mutate(cartItems.map(cartItem => cartItem.id));
          }}
          title={
            <FormattedMessage
              id="mainCartItems.clearCart.title"
              defaultMessage="Clear your cart?"
            />
          }
          message={
            <FormattedMessage
              id="mainCartItems.clearCart.subtitle"
              defaultMessage="Are you sure you want to delete {value, plural, =1 {this card} other {these # cards}} from your cart? This cannot be undone"
              values={{ value: cartItems.length }}
            />
          }
          cta={formatMessage(cart.clearAll)}
        >
          <FormattedMessage
            id="cartItems.clearCart"
            defaultMessage="Clear cart"
          />
        </ClearButton>
      </Header>
      <Section className={classNames({ 'clear-cart': removeFromCart.loading })}>
        <CartItems cartItems={availableCartItems} count={cartCount} />
        {!!cartCount && !!removedCartCount && <HorizontalDivider />}
        <CartItems cartItems={removedCartItems} count={removedCartCount} />
      </Section>
    </Vertical>
  );
};

MainCartItems.fragments = {
  cartItem: gql`
    fragment MainCartItems_cartItem on CartItem {
      id
      card {
        slug
        ...CartItem_anyCard
      }
      ...CartItem_cartItem
    }
    ${CartItem.fragments.anyCard}
    ${CartItem.fragments.cartItem}
  ` as TypedDocumentNode<MainCartItems_cartItem>,
};
